import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';

export interface ScConversation {
  id: number;
  gid: number;
  appKey: string;
  customerUid: number;
  customerName: string;
  status: 'open' | 'closed';
  startedAt: string;
  expiresAt: string;
  lastMessageAt: string | null;
  closedAt: string | null;
}

export interface ScMessage {
  id: number;
  conversationId: number;
  senderType: 'customer' | 'agent' | 'system';
  respondedAs: string | null;
  body: string;
  clientMsgId: string;
  createdAt: string;
  readAt: string | null;
}

export interface ScAttachment {
  id: number;
  messageId: number;
  fileName: string;
  mimeType: string;
  size: number;
}

@Injectable({ providedIn: 'root' })
export class SupportCenterService {
  private readonly base = `/api/support-center`;

  private socket: Socket | null = null;
  private identity: string | null = null; // "UID:GID" the widget's state and socket belong to
  readonly identityChanged$ = new Subject<void>();
  readonly messages$ = new BehaviorSubject<ScMessage[]>([]);
  readonly conversation$ = new BehaviorSubject<ScConversation | null>(null);
  readonly connected$ = new BehaviorSubject<boolean>(false);
  readonly newConversationInitiated$ = new Subject<ScConversation>();
  readonly otherConversationMessage$ = new Subject<ScMessage>();
  readonly newAttachment$ = new Subject<ScAttachment>();
  readonly agentOnline$ = new BehaviorSubject<boolean>(false);
  readonly pendingCount$ = new BehaviorSubject<number>(0);

  constructor(private http: HttpClient) {}

  // ─── REST ────────────────────────────────────────────────────────────────────
  listConversations(): Observable<(ScConversation & { lastBody: string | null })[]> {
    return this.http.get<any[]>(`${this.base}/conversations`);
  }

  getConversation(): Observable<ScConversation | null> {
    return this.http.get<ScConversation | null>(`${this.base}/conversation`);
  }

  markRead(conversationId: number): void {
    this.http.patch(`${this.base}/conversation/${conversationId}/read`, {}).subscribe({ error: () => {} });
    this.pendingCount$.next(0);
  }

  getPending(): void {
    this.http.get<{ unreadCount: number }>(`${this.base}/pending`).subscribe({
      next: ({ unreadCount }) => this.pendingCount$.next(unreadCount),
      error: () => {},
    });
  }

  startConversation(): Observable<ScConversation> {
    return this.http.post<ScConversation>(`${this.base}/conversation`, {});
  }

  getThread(id: number): Observable<{ conversation: ScConversation; messages: ScMessage[]; attachments: ScAttachment[] }> {
    return this.http.get<any>(`${this.base}/conversation/${id}`);
  }

  sendMessageHttp(conversationId: number, body: string, clientMsgId: string): Observable<ScMessage> {
    return this.http.post<ScMessage>(`${this.base}/conversation/${conversationId}/messages`, { conversationId, body, clientMsgId });
  }

  uploadAttachment(conversationId: number, messageId: number, file: File): Observable<ScAttachment> {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('conversationId', String(conversationId));
    fd.append('messageId', String(messageId));
    return this.http.post<ScAttachment>(`${this.base}/conversation/${conversationId}/attachments`, fd);
  }

  getAttachmentUrl(id: number): Observable<{ url: string }> {
    return this.http.get<{ url: string }>(`${this.base}/attachments/${id}/url`);
  }

  // ─── Socket ──────────────────────────────────────────────────────────────────
  // The server routes every event for this user's conversations to this one socket;
  // only events for the conversation on screen are applied here.
  connectSocket(): void {
    if (this.socket) return;
    this.socket = io(window.location.origin, {
      path: '/sc-customer',
      // WebSocket only: behind the load balancer a polling session would be pinned to the copy
      // that created it, and the next request could land on another copy ("Session ID unknown").
      transports: ['websocket'],
      withCredentials: true,
      forceNew: true, // a new handshake every time, so the server reads the current session
      reconnection: true,
      reconnectionDelay: 2000,
    });

    const isCurrent = (conversationId: number) => {
      const conv = this.conversation$.value;
      return !!conv && Number(conv.id) === Number(conversationId);
    };

    this.socket.on('connect',    () => this.connected$.next(true));
    this.socket.on('disconnect', () => this.connected$.next(false));

    this.socket.on('new_message', (msg: ScMessage) => {
      if (!isCurrent(msg.conversationId)) {
        this.otherConversationMessage$.next(msg);
        return;
      }
      if (this.messages$.value.some(m => m.id === msg.id)) return;
      this.messages$.next([...this.messages$.value, msg]);
    });

    this.socket.on('new_attachment', (att: ScAttachment) => this.newAttachment$.next(att));

    this.socket.on('conv_closed', ({ conversationId }: { conversationId: number }) => {
      const conv = this.conversation$.value;
      if (conv && isCurrent(conversationId)) this.conversation$.next({ ...conv, status: 'closed' });
    });

    this.socket.on('conversation_initiated', (payload: { conversation: ScConversation; message: ScMessage | null }) => {
      if (!payload?.conversation) return;
      this.conversation$.next(payload.conversation);
      this.messages$.next(payload.message ? [payload.message] : []);
      this.newConversationInitiated$.next(payload.conversation);
    });

    this.socket.on('agent_viewing', ({ conversationId }: { conversationId: number }) => {
      if (isCurrent(conversationId)) this.agentOnline$.next(true);
    });
    this.socket.on('agent_left', ({ conversationId }: { conversationId: number }) => {
      if (isCurrent(conversationId)) this.agentOnline$.next(false);
    });
  }

  // Call when customer opens the widget / enters a conversation
  notifyCustomerOnline(conversationId: number): void {
    this.socket?.emit('customer_online', conversationId);
  }

  // Call when customer closes the widget
  notifyCustomerOffline(conversationId: number): void {
    this.socket?.emit('customer_offline', conversationId);
  }

  disconnectSocket(): void {
    this.socket?.disconnect();
    this.socket = null;
  }

  // Tear down the socket and wipe all per-user state. Called on logout so a
  // singleton (providedIn: 'root') service never carries the previous user's
  // conversation/messages into the next login on a shared machine.
  reset(): void {
    this.disconnectSocket();
    this.messages$.next([]);
    this.conversation$.next(null);
    this.connected$.next(false);
    this.agentOnline$.next(false);
    this.pendingCount$.next(0);
    this.identity = null;
  }

  // Called by LoginService whenever the user or clinic is set: login, impersonation, and the
  // clinic picked after login (the session starts on the user's first clinic). The widget
  // stays mounted through all of them, but the chat socket's identity (UID + clinic) is
  // fixed at its handshake: drop what was shown for the previous identity and reconnect so
  // the new handshake carries the current one.
  setIdentity(uid: number | string | null, gid?: number | string | null): void {
    const id = uid ? `${uid}:${gid ?? ''}` : null;
    const previous = this.identity;
    if (id === previous) return;
    if (previous !== null) {
      const wasConnected = !!this.socket;
      this.reset();
      this.identityChanged$.next();
      if (wasConnected && id) {
        this.connectSocket();
        this.getPending();
      }
    }
    this.identity = id;
  }

  isExpired(conv: ScConversation): boolean {
    return conv.status === 'closed' || new Date(conv.expiresAt) < new Date();
  }

  // RFC 4122 v4 id for message idempotency. getRandomValues (unlike randomUUID)
  // also works on plain-http clinic LAN installs, which are not secure contexts.
  newClientMsgId(): string {
    const b = crypto.getRandomValues(new Uint8Array(16));
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    const h = Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
  }
}
